<template>
  <fragment v-if="campaign">
    <div class="channel-edit-group channel-edit-group-fields">
      <div class="field-w">
        <b-field :label="$t('Projects.RepeatPeriod')">
          <b-dropdown v-model="campaign.CommitmentRepeatPeriodID" scrollable aria-role="list" :disabled="campaignEditGoalsData.RepeatPeriods.length === 0"
                :label="$t('Projects.RepeatPeriod')"
                @active-change="onDropdownActiveChange">
            <template #trigger>
              <b-button>
                <span class="dropdown-trigger-value">
                  {{ selectedProjectRepeatPeriodItem ? selectedProjectRepeatPeriodItem.Text : $t("Projects.RepeatPeriod") }}
                </span>
                <icon-base width="12" height="7" view-box="0 0 12 7" icon-name="caret-down"><icon-caret-down /></icon-base>
              </b-button>
            </template>
            <b-dropdown-item v-for="repeatPeriod in campaignEditGoalsData.RepeatPeriods" :key="+repeatPeriod.Value" :value="+repeatPeriod.Value" aria-role="listitem">
              <span>{{ repeatPeriod.Text }}</span>
            </b-dropdown-item>
          </b-dropdown>
        </b-field>
      </div>
      <div class="field-w">
        <b-field :label="$t('Projects.Type')">
          <b-dropdown v-model="campaign.CommitmentTypeID" scrollable aria-role="list" :disabled="campaignEditGoalsData.CommitmentTypes.length === 0"
                :label="$t('Projects.Type')"
                @active-change="onDropdownActiveChange">
            <template #trigger>
              <b-button>
                <span class="dropdown-trigger-value">
                  {{ selectedProjectTypeItem ? selectedProjectTypeItem.Text : $t("Projects.Type") }}
                </span>
                <icon-base width="12" height="7" view-box="0 0 12 7" icon-name="caret-down"><icon-caret-down /></icon-base>
              </b-button>
            </template>
            <b-dropdown-item v-for="type in campaignEditGoalsData.CommitmentTypes" :key="+type.Value" :value="+type.Value" aria-role="listitem">
              <span>{{ type.Text }}</span>
            </b-dropdown-item>
          </b-dropdown>
        </b-field>
      </div>
      <div class="field-w" v-if="campaign.CommitmentRepeatPeriodID === CommitmentRepeatPeriod.None">
        <b-field :label="$t('CampaignEdit.StartDate')">
          <date-picker v-model="campaign.StartDate" format="DD/MM/YYYY" :append-to-body="false" :lang="lang" :placeholder="$t('Projects.StartDatePlaceholder')"
                class="field-dt" @open="onDatepickerOpen">
            <template #icon-calendar>
              <icon-base width="18" height="19" view-box="0 0 18 19" icon-name="calendar"><icon-calendar /></icon-base>
            </template>
          </date-picker>
        </b-field>
      </div>
      <div class="field-w" v-if="campaign.CommitmentRepeatPeriodID === CommitmentRepeatPeriod.None">
        <b-field :label="$t('CampaignEdit.EndDate')">
          <date-picker v-model="campaign.EndDate" format="DD/MM/YYYY" :append-to-body="false" :lang="lang" :placeholder="$t('Projects.EndDatePlaceholder')"
                class="field-dt" @open="onDatepickerOpen">
            <template #icon-calendar>
              <icon-base width="18" height="19" view-box="0 0 18 19" icon-name="calendar"><icon-calendar /></icon-base>
            </template>
          </date-picker>
        </b-field>
      </div>
      <div class="field-w">
        <b-field :label="$t('CampaignEdit.GoalField')">
          <b-input type="number" v-model="campaign.Goal" :placeholder="$t('CampaignEdit.GoalPlaceholder')"></b-input>
        </b-field>
      </div>
      <div class="field-w">
        <b-field :label="$t('CampaignEdit.Budget')">
          <b-input type="number" v-model="campaign.Budget" :placeholder="$t('CampaignEdit.BudgetPlaceholder')"></b-input>
        </b-field>
      </div>
    </div>
  </fragment>
</template>

<script lang="ts">
import Fragment from "vue-fragment";
import { Component, Vue, Watch } from "vue-property-decorator";
import DatePicker from "vue2-datepicker";
import "vue2-datepicker/index.css";
import "vue2-datepicker/locale/he";
import { Getter } from "vuex-class";
import i18n from "@/i18n";
import {  SelectListItem } from "@/models/MultiselectItem";
import { Campaign, CampaignEditViewModel } from "@/models/campaigns/Campaign";
import { CampaignEditGoalsData } from "@/models/campaigns/CampaignEditGoalsData";
import { CampaignEditPageKind } from "@/models/campaigns/CampaignEditPageKind";
import { CommitmentRepeatPeriod } from "@/models/projects/CommitmentRepeatPeriod";
import CampaignService from "@/services/CampaignService";
import HelperService from "@/services/HelperService";
import CampaignsApiService from "@/services/api/CampaignsApiService";
import IconBase from "../IconBase.vue";
import IconCalendar from "../icons/IconCalendar.vue";
import IconCaretDown from "../icons/IconCaretDown.vue";

Vue.use(Fragment.Plugin);
const namespace = "campaignsModule";

@Component({
  components: {
    DatePicker,
    IconBase,
    IconCalendar,
    IconCaretDown,
  },
})
export default class TabGoal extends Vue {

  //#region Vuex getters and actions

  @Getter("campaignEditViewModel", { namespace }) campaignEditViewModel!: CampaignEditViewModel;

  //#endregion Vuex getters and actions


  //#region Properties

  campaignEditGoalsData?: CampaignEditGoalsData = {
    CommitmentRepeatPeriodID: CommitmentRepeatPeriod.None,
    CommitmentTypes: [],
    RepeatPeriods: []
  };

  CampaignEditPageKind = CampaignEditPageKind;
  CommitmentRepeatPeriod = CommitmentRepeatPeriod;

  //#endregion Properties


  //#region Watchers

  @Watch("campaignEditViewModel", { immediate: true })
  onCampaignEditViewModelChange(newCampaignEditViewModel: CampaignEditViewModel, oldCampaignEditViewModel: CampaignEditViewModel): void {
    if (!newCampaignEditViewModel) {
      return;
    }
    CampaignsApiService.getCampaignEditGoalsData(false).then((data: CampaignEditGoalsData) => {
      this.campaignEditGoalsData = data;
    });
  }

  //#endregion Watchers


  //#region Getters

  get campaign(): Campaign {
    return this.campaignEditViewModel?.Campaign;
  }

  get lang(): string {
    return HelperService.getLanguageCodeWithoutCountry(i18n.locale);
  }

  get pageKind(): CampaignEditPageKind {
    return CampaignService.getCampaignEditPageKind(this.$route.path);
  }

  get selectedProjectRepeatPeriodItem(): SelectListItem | undefined {
    return this.campaignEditGoalsData?.RepeatPeriods.find((i: SelectListItem) => +i.Value === this.campaign.CommitmentRepeatPeriodID);
  }

  get selectedProjectTypeItem(): SelectListItem | undefined {
    return this.campaignEditGoalsData?.CommitmentTypes.find((i: SelectListItem) => +i.Value === this.campaign.CommitmentTypeID);
  }

  //#endregion Getters


  //#region Handlers

  onDropdownActiveChange(show: boolean) {
    HelperService.onDropdownActiveChange(show);
  }

  onDatepickerOpen(event: MouseEvent) {
    // if no space to the right of the page, popup will move to the left, so we need to draw arrow at another side
    setTimeout(() => {
      const popup = (document.getElementsByClassName("mx-datepicker-popup"))[0] as HTMLDivElement;
      if (popup.style.left !== "0px") {
        popup.classList.add("leftoffset");
      }
    });
  }

  //#endregion Handlers
}
</script>

<style lang="scss" scoped>
@import "@/assets/styles/variables";

.channel-edit-group {
  margin-bottom: 25px;

  &.channel-edit-group-field {
    max-width: 894px;

    .channel-edit-field {
      margin-bottom: 1rem;
    }

    .label {
      font-size: 17px;
      margin: 0;
      display: inline;
    }
  }

  &.channel-edit-group-fields {
    display: flex;
    flex-wrap: wrap;
    margin: 0 -12px;

    .field-w {
      padding: 0 12px 25px;
      flex-grow: 1;
      width: 50%;

      @media (max-width: $bp_md) {
        padding-bottom: 1rem;
        width: 100%;
      }

      .dropdown {
        width: 100%;

        .dropdown-item-with-icon {
          display: flex;

          .icon-w {
            display: flex;
            margin-inline-end: 0.5rem;
            min-width: 22px;

            img {
              max-height: 22px;
              min-width: 22px;
              width: 22px;
            }
          }
        }

        .dropdown-trigger {

          .button {
            width: 100%;
          }
        }
      }

      .mx-datepicker {
        width: 100%;
      }
    }
  }
}


</style>

<style lang="scss">
.field-dt {
  .mx-datepicker-popup {
    width: auto;

    &.leftoffset {
      &:before {
        left: auto;
        right: 34px;
      }

      &:after {
        left: auto;
        right: 33px;
      }
    }
  }
}
</style>